import { useParams } from 'react-router-dom'

import type { RenderCustomFieldProps } from '@factorialco/f0-react'
import { F0Dialog, F0Form, F0Select, useF0Form } from '@factorialco/f0-react'

import i18n from 'modules/core/lib/i18n'

import { Salary, Workplace } from 'modules/job_catalog/lib/salary'

import { useForm } from './form'

interface Props {
  onClose: () => void
  onSubmit: () => void
  createAction: (salary: Salary, nodeUuid: string) => Promise<boolean>
  updateAction: (salary: Salary, nodeUuid: string) => Promise<boolean>
  name: string
  nodeType: string
  initialSalary?: Salary
  alreadySelectedWorkplaces: Workplace[]
  hasAllWorkplacesSalary: boolean
}

export const isAllWorkplacesSalary = (salary?: Salary) =>
  !!salary && (!salary.workplaces || salary.workplaces.length === 0)

export const getAlreadySelectedInOtherSalariesWorkplaces = (
  alreadySelectedWorkplaces: Workplace[],
  initialSalary?: Salary
) => {
  if (!initialSalary) return alreadySelectedWorkplaces

  if (isAllWorkplacesSalary(initialSalary)) return alreadySelectedWorkplaces

  const currentSalaryWorkplaceIds = new Set(
    initialSalary.workplaces?.map((workplace) => workplace.id)
  )

  return alreadySelectedWorkplaces.filter(
    (workplace) => !currentSalaryWorkplaceIds.has(workplace.id)
  )
}

export const getValuesForTypeChange = (values: Record<string, unknown>, nextType: string) => {
  if (values.type === nextType) return values

  return {
    ...values,
    type: nextType,
    minAmount: undefined,
    maxAmount: undefined,
    grossSalary: undefined,
  }
}

const SalaryModal = ({
  name,
  onClose,
  createAction,
  updateAction,
  onSubmit,
  initialSalary,
  alreadySelectedWorkplaces,
  hasAllWorkplacesSalary,
}: Props) => {
  const params = useParams()
  const { rootId, familyId, functionId, roleId, levelId, id } = params
  const nodeId = levelId || roleId || functionId || familyId || rootId || id || ''
  const { formRef, submit, isSubmitting, getValues, setValues, trigger } = useF0Form()

  const title = i18n.t('job_catalog.nodes.salary_modal.title', { name })
  const isEditingAllWorkplacesSalary = isAllWorkplacesSalary(initialSalary)
  const hasAllWorkplacesSalaryInOtherSalaries =
    hasAllWorkplacesSalary && !isEditingAllWorkplacesSalary

  const alreadySelectedInOtherSalariesWorkplaces = getAlreadySelectedInOtherSalariesWorkplaces(
    alreadySelectedWorkplaces,
    initialSalary
  )

  const { formDefinition } = useForm({
    name,
    initialSalary,
    availableWorkplaces: alreadySelectedWorkplaces,
    disabledWorkplaces: alreadySelectedInOtherSalariesWorkplaces,
    workplacesMandatory: hasAllWorkplacesSalaryInOtherSalaries,
    createAction,
    updateAction,
    nodeId,
    onSubmit,
  })

  const validateAndSubmit = async () => {
    const isValid = await trigger()

    if (!isValid) {
      return
    }

    await submit()
  }

  const handleSubmit = () => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur()
    }

    // Let blur-driven validation update form state before submit validation runs.
    queueMicrotask(() => {
      void validateAndSubmit()
    })
  }

  const renderCustomField = (props: RenderCustomFieldProps) => {
    if (props.customFieldName !== 'salary-type-select') return null

    const options = Array.isArray(props.config) ? props.config : []

    return (
      <F0Select
        size="md"
        label={props.label}
        value={typeof props.value === 'string' ? props.value : ''}
        options={options}
        disabled={props.disabled}
        error={props.error}
        onChange={(nextValue: string | string[]) => {
          const currentValues = getValues()
          const nextValues = getValuesForTypeChange(currentValues, String(nextValue))

          if (nextValues === currentValues) {
            props.onChange(nextValue)
            return
          }

          setValues(nextValues, { shouldDirty: true, shouldValidate: false })
        }}
      />
    )
  }

  return (
    <F0Dialog
      isOpen
      onClose={onClose}
      width="sm"
      title={title}
      primaryAction={{
        label: i18n.t('job_catalog.nodes.salary_modal.submit'),
        onClick: handleSubmit,
        loading: isSubmitting,
        disabled: isSubmitting,
      }}
      secondaryAction={{
        label: i18n.t('job_catalog.nodes.salary_modal.cancel'),
        onClick: onClose,
      }}
    >
      <F0Form
        formRef={formRef}
        formDefinition={formDefinition}
        renderCustomField={renderCustomField}
      />
    </F0Dialog>
  )
}

export default SalaryModal
